import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import qs from "query-string";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";

import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation } from "@apollo/client";

import { QUERY_TEST_ACTIVE_DIRECTORY_APP_ONLY } from "../../../../config/graphql/query";
import { CONNECT_ACTIVE_DIRECTORY_APP_ONLY } from "../../../../config/graphql/mutation";

import { getApiUrl } from "../../../../lib/common/getApiUrl";

import {
  clearConsentDraft,
  saveConsentDraft,
  IActiveDirectoryConsentDraft,
} from "./consentDraft";

interface ConnectAppOnlyModalProps {
  clientId: string;
  visible: boolean;
  onHide: () => void;
  /** Set when returning from the consent screen. */
  initial?: IActiveDirectoryConsentDraft | null;
  consentGranted?: boolean;
  consentError?: string | null;
}

// Entra needs up to a minute to spread a fresh consent: retried quietly.
const AUTO_RETRIES = 6;
const AUTO_RETRY_MS = 10000;

/** Counterpart of `Outlook/ConnectAppOnlyModal`: consent → test → connect. */
const ConnectAppOnlyModal = React.memo(
  ({
    clientId,
    visible,
    onHide,
    initial,
    consentGranted,
    consentError,
  }: ConnectAppOnlyModalProps) => {
    const { t } = useTranslation(["client", "common"]);

    const [tenantId, setTenantId] = useState("");
    const [email, setEmail] = useState("");
    const [title, setTitle] = useState("");
    const [tested, setTested] =
      useState<IActiveDirectoryAppOnlyTestResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    // The return from Microsoft is a fresh page load, so the test runs by itself.
    const autoTested = useRef(false);
    const autoRetries = useRef(0);

    const apiUrl = getApiUrl();

    const consentUrl = useMemo(
      () =>
        qs.stringifyUrl(
          {
            url: `${apiUrl}/api/v1/activedirectory/auth/admin-consent`,
            query: {
              client: clientId,
              tenantId: tenantId.trim(),
              redirectUri: window.location.href,
            },
          },
          { encode: true },
        ),
      [apiUrl, clientId, tenantId],
    );

    const [runTest, { loading: testing }] = useLazyQuery<{
      testActiveDirectoryAppOnly: IActiveDirectoryAppOnlyTestResult;
    }>(QUERY_TEST_ACTIVE_DIRECTORY_APP_ONLY, {
      fetchPolicy: "network-only",
      context: { headers: { ClientId: clientId } },
      onCompleted: ({ testActiveDirectoryAppOnly }) => {
        setTested(testActiveDirectoryAppOnly);
        setError(
          testActiveDirectoryAppOnly.ok
            ? null
            : testActiveDirectoryAppOnly.message ?? null,
        );
      },
      onError: (e) => {
        setTested(null);
        setError(e.message);
      },
    });

    const [connect, { loading: connecting }] = useMutation(
      CONNECT_ACTIVE_DIRECTORY_APP_ONLY,
      {
        // By name, so the list refetches with its own ClientId header.
        refetchQueries: ["activeDirectories"],
        context: { headers: { ClientId: clientId } },
        onCompleted: () => {
          clearConsentDraft(clientId);
          onHide();
        },
        onError: (e) => {
          // A failed attempt voids the earlier test.
          setTested(null);
          setError(e.message);
        },
      },
    );

    const canTest = !!tenantId.trim() && !testing;

    // An edit invalidates the test, so one organisation cannot be tested and another connected.
    const onChange = useCallback(
      (setter: (value: string) => void, invalidates = true) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
          setter(event.target.value);

          if (invalidates) {
            setTested(null);
            setError(null);
          }
        },
      [],
    );

    const onTest = useCallback(() => {
      setError(null);
      runTest({ variables: { tenantId: tenantId.trim() } });
    }, [runTest, tenantId]);

    const onConnect = useCallback(() => {
      setError(null);
      connect({
        variables: {
          input: {
            tenantId: tenantId.trim(),
            email: email.trim(),
            ...(title.trim() ? { title: title.trim() } : {}),
          },
        },
      });
    }, [connect, tenantId, email, title]);

    const onClose = useCallback(() => {
      setTested(null);
      setError(null);
      clearConsentDraft(clientId);
      onHide();
    }, [onHide, clientId]);

    // Back from the consent screen: refill the form, show a refusal from the URL.
    useEffect(() => {
      if (!initial) {
        return;
      }

      if (initial.tenantId) setTenantId(initial.tenantId);
      if (initial.email) setEmail(initial.email);
      if (initial.title) setTitle(initial.title);

      setError(consentError ?? null);
      autoTested.current = false;
      autoRetries.current = 0;
    }, [initial, consentError]);

    useEffect(() => {
      if (
        !visible ||
        !consentGranted ||
        consentError ||
        autoTested.current ||
        !tenantId.trim()
      ) {
        return;
      }

      autoTested.current = true;
      runTest({ variables: { tenantId: tenantId.trim() } });
    }, [visible, consentGranted, consentError, tenantId, runTest]);

    // Right after the consent Entra may still be catching up.
    useEffect(() => {
      if (
        !visible ||
        !consentGranted ||
        !tested ||
        tested.ok ||
        autoRetries.current >= AUTO_RETRIES
      ) {
        return undefined;
      }

      autoRetries.current += 1;

      const timer = window.setTimeout(() => {
        runTest({ variables: { tenantId: tenantId.trim() } });
      }, AUTO_RETRY_MS);

      return () => window.clearTimeout(timer);
    }, [visible, consentGranted, tested, tenantId, runTest]);

    // No red while the quiet retries still run.
    const autoChecking =
      consentGranted &&
      !tested?.ok &&
      (testing || (!!tested && autoRetries.current < AUTO_RETRIES));

    return (
      <Modal show={visible} onHide={onClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {t(
              "client:client.integrations.activeDirectory.appOnly.modal.title",
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            {t(
              "client:client.integrations.activeDirectory.appOnly.modal.intro",
            )}
          </p>

          <h6 className="mb-1">
            {t(
              "client:client.integrations.activeDirectory.appOnly.modal.steps.oneTitle",
            )}
          </h6>
          <p className="text-muted small mb-2">
            {t(
              "client:client.integrations.activeDirectory.appOnly.modal.steps.oneText",
            )}
          </p>

          <Form.Group>
            <Form.Label>
              {t(
                "client:client.integrations.activeDirectory.appOnly.field.tenantId",
              )}
            </Form.Label>
            <Form.Control
              value={tenantId}
              onChange={onChange(setTenantId)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
            <Form.Text className="text-muted">
              {t(
                "client:client.integrations.activeDirectory.appOnly.field.tenantIdHelp",
              )}
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>
              {t(
                "client:client.integrations.activeDirectory.appOnly.field.email",
              )}
            </Form.Label>
            <Form.Control
              type="email"
              value={email}
              // Not part of what the test checks, so it does not reset it.
              onChange={onChange(setEmail, false)}
              placeholder="it-admin@yourcompany.com"
            />
            <Form.Text className="text-muted">
              {t(
                "client:client.integrations.activeDirectory.appOnly.field.emailHelp",
              )}
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>
              {t(
                "client:client.integrations.activeDirectory.appOnly.field.title",
              )}
            </Form.Label>
            <Form.Control value={title} onChange={onChange(setTitle, false)} />
            <Form.Text className="text-muted">
              {t(
                "client:client.integrations.activeDirectory.appOnly.field.titleHelp",
              )}
            </Form.Text>
          </Form.Group>

          <hr className="my-3" />

          <h6 className="mb-1">
            {t(
              "client:client.integrations.activeDirectory.appOnly.modal.steps.twoTitle",
            )}
          </h6>
          <p className="text-muted small mb-2">
            {t(
              "client:client.integrations.activeDirectory.appOnly.modal.steps.twoText",
            )}
          </p>

          <div className="d-flex align-items-center mb-3">
            <a
              href={consentUrl}
              // Survives the reload on the way back from Microsoft.
              onClick={() =>
                saveConsentDraft(clientId, {
                  tenantId: tenantId.trim(),
                  email: email.trim(),
                  title: title.trim(),
                })
              }
              className={`btn btn-outline-primary mr-3 text-nowrap flex-shrink-0 ${
                tenantId.trim() ? "" : "disabled"
              }`}
            >
              {t(
                "client:client.integrations.activeDirectory.appOnly.button.consent",
              )}
            </a>
            <small className="text-muted">
              {t(
                "client:client.integrations.activeDirectory.appOnly.button.consentHelp",
              )}
            </small>
          </div>

          <hr className="my-3" />

          <h6 className="mb-1">
            {t(
              "client:client.integrations.activeDirectory.appOnly.modal.steps.threeTitle",
            )}
          </h6>
          <p className="text-muted small mb-2">
            {t(
              "client:client.integrations.activeDirectory.appOnly.modal.steps.threeText",
            )}
          </p>

          {autoChecking && (
            <Alert variant="info" className="small py-2 mb-2">
              {t(
                "client:client.integrations.activeDirectory.appOnly.consentReturned",
              )}
            </Alert>
          )}

          {tested?.ok && (
            <Alert variant="success">
              {t("client:client.integrations.activeDirectory.appOnly.tested")}
            </Alert>
          )}

          {error && !autoChecking && <Alert variant="danger">{error}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            {t("common:cancel")}
          </Button>
          <Button
            variant="outline-primary"
            disabled={!canTest}
            onClick={onTest}
          >
            {testing && (
              <Spinner animation="border" size="sm" className="mr-2" />
            )}
            {t(
              "client:client.integrations.activeDirectory.appOnly.button.test",
            )}
          </Button>
          <Button
            variant="primary"
            // The server checks again; this keeps the error on the form.
            disabled={!tested?.ok || !email.trim() || connecting}
            onClick={onConnect}
          >
            {connecting && (
              <Spinner animation="border" size="sm" className="mr-2" />
            )}
            {t(
              "client:client.integrations.activeDirectory.appOnly.button.connect",
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  },
);

export default ConnectAppOnlyModal;
